import { useEffect, useRef, useState } from 'react';
import {
    GearIcon,
    ListIcon,
    ListChecksIcon,
    PaperPlaneTiltIcon,
    QuestionIcon,
    ShieldIcon,
    SignOutIcon,
    SwatchesIcon,
    UserGearIcon,
    XIcon,
} from '@phosphor-icons/react';
import NavigationMenu from './components/NavigationMenu';
import NavigationListItem from './components/NavigationListItem';
import { SignOut as signOutFunction } from '../Utility/SignOut';
import getAdminLinks, { getComplianceLinks } from '../../navigationConfig';
import Avatar from '../Utility/Avatar';
import ThemeSwitcher from '../ThemeSwitcher/ThemeSwitcher';
import { setMobileMenuOpen } from '../../Store/features/navigationHeader/navigationHeaderSlice';
import ChatListMobile from '../ChatList/ChatListMobile/ChatListMobile';
import { ChatMessageRole } from '../../Types/Enums';
import { Button } from '../Utility/Button';
import { RootState, useAppDispatch, useAppSelector } from '../../store';
import { UserSettings } from '../../Types';
import DemoModal from '../Demo/DemoModal';
import useModal from '../Modal/useModal';
import { getBreakpoint } from '../../utils/breakpoint';

export default function NavigationHeader() {
    const windowWidth = useAppSelector((state: RootState) => state.environment.windowWidth);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const demo = useAppSelector((state: RootState) => state.environment.demoInstance as boolean);
    const dispatch = useAppDispatch();
    const userSettings = useAppSelector((state: RootState) => state.userSettings as UserSettings);
    const [openDropdown, setOpenDropdown] = useState<null | number>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const signOut = signOutFunction();
    const chatlistOpen = useAppSelector((state: RootState) => state.chatlist.chatlistOpen);
    const [activeMenu, setActiveMenu] = useState(0);
    const isMobileMenuOpen = useAppSelector((state: RootState) => state.navHeader.isMobileMenuOpen);
    const settings = useAppSelector((state: RootState) => state.settings);
    const messageSourceIdentifier = useAppSelector((state: RootState) => state.messageSource.messageIdentifier);

    const navigationTitle = useAppSelector((state: RootState) => state.navHeader.navigationTitle);
    const navigationSubTitle = useAppSelector((state: RootState) => state.navHeader.navigationSubTitle);
    const navigationImage = useAppSelector((state: RootState) => state.navHeader.navigationImage);
    const supportMailAddress = useAppSelector((state: RootState) => state.settings.supportMailAddress);

    const modal = useModal();

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isMobileMenuOpen]);

    const toggleMenu = () => {
        dispatch(setMobileMenuOpen(!isMobileMenuOpen));
        if (isMobileMenuOpen) {
            setTimeout(() => {
                setActiveMenu(0);
            }, 300);
        } else {
            setActiveMenu(0);
        }
    };

    const adminLinks = getAdminLinks(
        {
            isAdmin: userSettings.isAdmin || demo,
            isSuperAdmin: userSettings.isSuperAdmin,
            isAvency: userSettings.isAvency,
            isComplianceAdmin: userSettings.isComplianceAdmin,
            isMaintainer: userSettings.isMaintainer,
        },
        settings,
        i18n,
        demo,
    );

    const complianceLinks = getComplianceLinks(i18n);

    const toggleDropdown = (index: number | null) => {
        setOpenDropdown(openDropdown === index ? null : index);
    };

    const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (dropdownRef.current && !dropdownRef.current.contains(target) && !target.closest('.navigationMenuButton')) {
            setOpenDropdown(null);
        }
    };

    function calculateWidthStyle(): React.CSSProperties {
        if (chatlistOpen) {
            if (messageSourceIdentifier !== '') {
                return { width: 'calc(100% - var(--chatlist-width, 300px) - 400px)' };
            } else {
                return { width: 'calc(100% - var(--chatlist-width, 300px))' };
            }
        } else {
            if (messageSourceIdentifier !== '') {
                return { width: 'calc(100% - 400px)' };
            } else {
                return { width: '100%' };
            }
        }
    }

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const menuTabs = [
        i18n.translate({
            id: 'navigationHeader.tabChats',
            sourceName: 'NavigationHeader',
        }),
        i18n.translate({
            id: 'navigationHeader.tabMenu',
            sourceName: 'NavigationHeader',
        }),
    ];

    const navigationHeaderIcon = adminLinks.find((link) => link.to === window.location.pathname)?.navigationHeaderIcon;

    return (
        <>
            {windowWidth >= getBreakpoint('xl') ? (
                <div
                    className={`hidden xl:block xl:fixed z-[10] top-0 h-[100px] bg-navHeader-background border-b border-navHeader-border`}
                    style={calculateWidthStyle()}
                >
                    <div className={` transition-all duration-300 ${chatlistOpen ? 'pl-10' : 'pl-[100px]'}`}>
                        <div>
                            {navigationTitle !== '' && (
                                <div className="flex w-full h-[100px]">
                                    <div className="flex gap-5 items-center overflow-hidden">
                                        {window.location.pathname !== '/' &&
                                            !window.location.pathname.startsWith('/start') && (
                                                <>
                                                    <div
                                                        className={`navigationheader-icon-display absolute top-[24px] ${
                                                            chatlistOpen ? 'left-10' : 'left-[100px]'
                                                        }`}
                                                    >
                                                        <Avatar
                                                            role={ChatMessageRole.assistant}
                                                            avatarImage={navigationImage ?? ''}
                                                            icon={
                                                                window.location.pathname.includes('/chats')
                                                                    ? undefined
                                                                    : navigationHeaderIcon
                                                            }
                                                            title={
                                                                window.location.pathname.includes('/chats')
                                                                    ? navigationSubTitle
                                                                    : navigationTitle
                                                            }
                                                        />
                                                    </div>
                                                    <div
                                                        className={`navigationheader-icon-padding flex flex-col h-fit overflow-hidden`}
                                                    >
                                                        {navigationTitle !== '' && (
                                                            <h3 className="m-0 p-0 truncate">
                                                                {window.location.pathname.includes('/chats')
                                                                    ? navigationSubTitle
                                                                    : navigationTitle}
                                                            </h3>
                                                        )}
                                                        {window.location.pathname.includes('/chats') && (
                                                            <span className="text-body block truncate text-navHeader-subline-color max-w-[calc(100%-300px)] min-w-[300px]">
                                                                {navigationTitle}
                                                            </span>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="absolute top-0 right-0 p-6 flex items-center justify-end h-[100px]">
                        <div className="flex space-x-2 items-center">
                            {demo && (
                                <div className="flex items-center gap-2">
                                    <Button
                                        onClick={() =>
                                            modal.open({
                                                title: i18n.translate({
                                                    id: 'modal.headline',
                                                    sourceName: 'Demo',
                                                }),
                                                size: 'large',
                                                content: <DemoModal />,
                                            })
                                        }
                                    >
                                        {i18n.translate({
                                            id: 'navigationHeader.demoMode.button',
                                            sourceName: 'NavigationHeader',
                                        })}
                                    </Button>
                                </div>
                            )}

                            {userSettings.isComplianceAdmin && (
                                <>
                                    <NavigationMenu
                                        toggleDropdown={toggleDropdown}
                                        openDropdown={openDropdown}
                                        dropdownRef={dropdownRef}
                                        menuNumber={1}
                                        headline={i18n.translate({
                                            id: 'compliance.title',
                                            sourceName: 'LogFiles',
                                        })}
                                        icon={<ListChecksIcon size={24} />}
                                    >
                                        <ul className="py-0 my-0 pl-0 list-none">
                                            <>
                                                {complianceLinks.map((link, index) => {
                                                    return (
                                                        <NavigationListItem
                                                            key={index}
                                                            link={link.to}
                                                            menuItemText={link.label}
                                                            icon={link.icon}
                                                            toggleDropdown={toggleDropdown}
                                                            openDropdown={openDropdown}
                                                        />
                                                    );
                                                })}
                                            </>
                                        </ul>
                                    </NavigationMenu>
                                </>
                            )}

                            {(userSettings.isAdmin || userSettings.isSuperAdmin || demo) && (
                                <>
                                    <NavigationMenu
                                        toggleDropdown={toggleDropdown}
                                        openDropdown={openDropdown}
                                        dropdownRef={dropdownRef}
                                        menuNumber={2}
                                        headline={i18n.translate({
                                            id: 'navigationHeader.adminMenuHeadline',
                                            sourceName: 'NavigationHeader',
                                        })}
                                        icon={<GearIcon size={24} />}
                                    >
                                        <ul className="py-0 my-0 pl-0 list-none">
                                            <>
                                                {adminLinks.map((link, index) => {
                                                    return (
                                                        <NavigationListItem
                                                            key={index}
                                                            link={link.to}
                                                            menuItemText={link.label}
                                                            icon={link.icon}
                                                            toggleDropdown={toggleDropdown}
                                                            openDropdown={openDropdown}
                                                        />
                                                    );
                                                })}
                                            </>
                                        </ul>
                                    </NavigationMenu>
                                </>
                            )}

                            <NavigationMenu
                                toggleDropdown={toggleDropdown}
                                openDropdown={openDropdown}
                                dropdownRef={dropdownRef}
                                menuNumber={3}
                                headline={i18n.translate({
                                    id: 'navigationHeader.userMenuHeadline',
                                    sourceName: 'NavigationHeader',
                                })}
                                icon={<Avatar role={ChatMessageRole.user} size={50} />}
                                isUserProfile={true}
                            >
                                <ul className="p-0 m-0 list-none">
                                    <li className="py-0 my-0">
                                        <div className="flex items-start gap-4">
                                            <div
                                                className={`relative rounded-full h-[50px] text-xs w-[50px] flex items-center justify-center`}
                                            >
                                                <Avatar role={ChatMessageRole.user} size={50} />
                                            </div>
                                            <div className="my-0 flex flex-col justify-between">
                                                <p className="p-0 m-0 text-lg">
                                                    {userSettings.givenName} {userSettings.familyName}
                                                </p>
                                                <p className="p-0 m-0 text-sm text-gray-500">{userSettings.email}</p>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                                <hr className="border-t border-layout-borderColor w-full mb-0 mt-0"></hr>
                                <NavigationListItem
                                    link="/usersettings"
                                    menuItemText={i18n.translate({
                                        id: 'navigationHeader.editAccountSettings',
                                        sourceName: 'NavigationHeader',
                                    })}
                                    icon={<UserGearIcon size={24} />}
                                    toggleDropdown={toggleDropdown}
                                    openDropdown={openDropdown}
                                    noPadding={true}
                                />
                                <NavigationListItem
                                    click={() => {
                                        modal.open({
                                            title: i18n.translate({
                                                id: 'themeswitcher.title',
                                                sourceName: 'ThemeSwitcher',
                                            }),
                                            content: <ThemeSwitcher />,
                                            size: 'medium',
                                        });
                                    }}
                                    menuItemText={i18n.translate({
                                        id: 'themeswitcher.title',
                                        sourceName: 'ThemeSwitcher',
                                    })}
                                    icon={<SwatchesIcon size={24} />}
                                    toggleDropdown={toggleDropdown}
                                    openDropdown={openDropdown}
                                    noPadding={true}
                                />
                                {settings.helpLinkVisible && (
                                    <NavigationListItem
                                        click={() => {
                                            window.open('https://varios-ai.com/hilfe/', '_blank');
                                        }}
                                        menuItemText={i18n.translate({
                                            id: 'help',
                                            sourceName: 'NavigationHeader',
                                        })}
                                        icon={<QuestionIcon size={24} />}
                                        toggleDropdown={toggleDropdown}
                                        openDropdown={openDropdown}
                                        noPadding={true}
                                    />
                                )}
                                <NavigationListItem
                                    click={() => {
                                        window.open('https://varios-ai.com/newsletter/', '_blank');
                                    }}
                                    menuItemText={i18n.translate({
                                        id: 'newsletter',
                                        sourceName: 'NavigationHeader',
                                    })}
                                    icon={<PaperPlaneTiltIcon size={24} />}
                                    toggleDropdown={toggleDropdown}
                                    openDropdown={openDropdown}
                                    noPadding={true}
                                />
                                {settings.dataProtectionText && settings.dataProtectionText.trim() !== '' && (
                                    <NavigationListItem
                                        link="/privacy"
                                        menuItemText={i18n.translate({
                                            id: 'privacy',
                                            sourceName: 'NavigationHeader',
                                        })}
                                        icon={<ShieldIcon size={24} />}
                                        toggleDropdown={toggleDropdown}
                                        openDropdown={openDropdown}
                                        noPadding={true}
                                    />
                                )}
                                <hr className="border-t border-layout-borderColor w-full mb-0 mt-0"></hr>
                                <ul className="py-0 m-0 px-0 list-none">
                                    <NavigationListItem
                                        click={() => signOut()}
                                        menuItemText={i18n.translate({
                                            id: 'navigationHeader.logout',
                                            sourceName: 'NavigationHeader',
                                        })}
                                        icon={<SignOutIcon size={24} />}
                                        toggleDropdown={toggleDropdown}
                                        openDropdown={openDropdown}
                                    />
                                </ul>
                            </NavigationMenu>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="fixed w-full border-0 border-b border-layout-borderColor block xl:hidden bg-navHeader-background-mobile z-[150] min-h-[70px] p-0 max-h-[calc(var(--app-height))] overflow-hidden">
                    <div>
                        <div className="absolute z-[300] top-0 h-[70px] flex items-center px-4 justify-between w-full text-layout-textcolor">
                            <div
                                onClick={toggleMenu}
                                className="h-[50px] w-[50px] min-h-[50px] min-w-[50px] flex items-center justify-center bg-navHeader-button rounded-full cursor-pointer hover:text-primary transition-all duration-100"
                            >
                                {isMobileMenuOpen ? <XIcon size={24} /> : <ListIcon size={24} />}
                            </div>
                            <h3 className="text-layout-textcolor px-6 m-0 flex-1 truncate">{navigationTitle}</h3>
                        </div>

                        <div
                            className={`transition-all z-[300] px-0 pb-0 duration-500 ease-in-out overflow-hidden flex flex-col pt-[70px] bg-chatlist-background ${
                                isMobileMenuOpen ? 'h-[var(--app-height)] opacity-100' : 'h-0 opacity-0'
                            }`}
                        >
                            <div className="px-10">
                                <div className="flex w-full pt-5 relative">
                                    <div
                                        style={{
                                            left: `${(100 / menuTabs.length) * activeMenu}%`,
                                            width: `${100 / menuTabs.length}%`,
                                        }}
                                        className="absolute z-[5] bottom-0 h-[2px] bg-primary transition-all duration-300 ease-in-out"
                                    ></div>
                                    <div className="absolute bottom-0 w-full h-[2px] bg-layout-borderColor transition-all duration-300 ease-in-out"></div>

                                    {menuTabs.map((tab, index) => (
                                        <div
                                            key={index}
                                            className={`relative z-[10] flex-1 text-center p-2 px-10 cursor-pointer ${
                                                index === activeMenu ? 'text-primary' : 'text-layout-textcolor'
                                            }`}
                                            onClick={() => setActiveMenu(index)}
                                        >
                                            {tab}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className={`${activeMenu === 0 ? 'block' : 'hidden'} overflow-y-auto`}>
                                <div className="text-layout-textcolor list-none grow p-0 m-0 pb-20 pt-2 px-10">
                                    <ChatListMobile />
                                </div>
                                <div className="px-10 pb-4 absolute bottom-0 bg-chatlist-background w-full z-[20] ">
                                    <hr className="border-t border-layout-borderColor w-full mb-3 mt-0"></hr>
                                    <NavigationListItem
                                        click={() => signOut()}
                                        menuItemText={i18n.translate({
                                            id: 'navigationHeader.logout',
                                            sourceName: 'NavigationHeader',
                                        })}
                                        icon={<SignOutIcon size={24} />}
                                        toggleDropdown={toggleDropdown}
                                        openDropdown={openDropdown}
                                    />
                                </div>
                            </div>

                            <div className={`${activeMenu === 1 ? 'block' : 'hidden'} overflow-y-auto`}>
                                <div className="flex items-start gap-4 px-10 pt-7">
                                    <div
                                        className={`relative rounded-full h-[50px] text-xs w-[50px] flex items-center justify-center`}
                                    >
                                        <Avatar role={ChatMessageRole.user} size={50} />
                                    </div>
                                    <div className="my-0 flex flex-col justify-between h-full py-1">
                                        <p className="p-0 m-0 text-md">
                                            {userSettings.givenName} {userSettings.familyName}
                                        </p>
                                        <p className="p-0 m-0 text-sm text-gray-500">{userSettings.email}</p>
                                    </div>
                                </div>
                                <div className="px-10">
                                    <hr className="border-t border-layout-borderColor w-full my-7 px-10"></hr>
                                </div>

                                <ul className="text-layout-textcolor flex flex-col gap-3 list-none grow p-0 m-0 pb-5 overflow-auto px-10">
                                    <>
                                        {adminLinks.map((link, index) => {
                                            return (
                                                <NavigationListItem
                                                    key={index}
                                                    link={link.to}
                                                    menuItemText={link.label}
                                                    icon={link.icon}
                                                    toggleDropdown={toggleDropdown}
                                                    openDropdown={openDropdown}
                                                />
                                            );
                                        })}
                                    </>
                                    {adminLinks.length > 0 && (
                                        <hr className="border-t border-layout-borderColor w-full"></hr>
                                    )}
                                    {userSettings.isComplianceAdmin && (
                                        <>
                                            <h6 className="m-0 p-0">
                                                {i18n.translate({ id: 'compliance.title', sourceName: 'LogFiles' })}
                                            </h6>
                                            {complianceLinks.map((link, index) => {
                                                return (
                                                    <NavigationListItem
                                                        key={index}
                                                        link={link.to}
                                                        menuItemText={link.label}
                                                        icon={link.icon}
                                                        toggleDropdown={toggleDropdown}
                                                        openDropdown={openDropdown}
                                                    />
                                                );
                                            })}
                                            <hr className="border-t border-layout-borderColor w-full"></hr>
                                        </>
                                    )}

                                    <NavigationListItem
                                        link="/usersettings"
                                        menuItemText={i18n.translate({
                                            id: 'navigationHeader.editAccountSettings',
                                            sourceName: 'NavigationHeader',
                                        })}
                                        icon={<UserGearIcon size={24} />}
                                        toggleDropdown={toggleDropdown}
                                        openDropdown={openDropdown}
                                    />
                                    <NavigationListItem
                                        click={() => {
                                            modal.open({
                                                title: i18n.translate({
                                                    id: 'themeswitcher.title',
                                                    sourceName: 'ThemeSwitcher',
                                                }),
                                                content: <ThemeSwitcher />,
                                                size: 'medium',
                                            });
                                        }}
                                        menuItemText={i18n.translate({
                                            id: 'themeswitcher.title',
                                            sourceName: 'ThemeSwitcher',
                                        })}
                                        icon={<SwatchesIcon size={24} />}
                                        toggleDropdown={toggleDropdown}
                                        openDropdown={openDropdown}
                                    />
                                    {supportMailAddress !== '' && (
                                        <NavigationListItem
                                            click={() => {
                                                window.location.href = 'mailto:' + supportMailAddress;
                                            }}
                                            menuItemText={i18n.translate({
                                                id: 'help',
                                                sourceName: 'NavigationHeader',
                                            })}
                                            icon={<QuestionIcon size={24} />}
                                            toggleDropdown={toggleDropdown}
                                            openDropdown={openDropdown}
                                        />
                                    )}
                                    {settings.dataProtectionText && settings.dataProtectionText.trim() !== '' && (
                                        <NavigationListItem
                                            link="/privacy"
                                            menuItemText={i18n.translate({
                                                id: 'privacy',
                                                sourceName: 'NavigationHeader',
                                            })}
                                            icon={<ShieldIcon size={24} />}
                                            toggleDropdown={toggleDropdown}
                                            openDropdown={openDropdown}
                                        />
                                    )}
                                </ul>
                                <div className="px-10 pb-4">
                                    <hr className="border-t border-layout-borderColor w-full mb-3 mt-0"></hr>
                                    <NavigationListItem
                                        click={() => signOut()}
                                        menuItemText={i18n.translate({
                                            id: 'navigationHeader.logout',
                                            sourceName: 'NavigationHeader',
                                        })}
                                        icon={<SignOutIcon size={24} />}
                                        toggleDropdown={toggleDropdown}
                                        openDropdown={openDropdown}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
